import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";
import invariant from "tiny-invariant";

import {
  Company,
  Deal,
  EntitySupportingCard,
  EntityType,
  Person,
} from "@mapmycustomers/shared/types/entity";
import Note from "@mapmycustomers/shared/types/entity/common/Note";
import { convertGeoPointToLatLngLiteral } from "@mapmycustomers/shared/util/geo/GeoService";

import GroupsModal from "@app/component/actions/components/GroupsModal";
import AddToRouteModal from "@app/component/AddToRouteModal";
import CompanyAssociation from "@app/component/associations/CompanyAssociation";
import DealAssociation from "@app/component/associations/DealAssociation";
import PeopleAssociation from "@app/component/associations/PeopleAssociation";
import AddNoteModal from "@app/component/createEditEntity/AddNoteModal";
import EditStageModal from "@app/component/createEditEntity/EditStageModal";
import {
  addCompanyChildCompanies,
  addCompanyDeals,
  addCompanyParentCompany,
  addCompanyPeople,
} from "@app/component/preview/CompanyRecordPane/store/actions";
import {
  addDealParentCompany,
  addDealParentPerson,
} from "@app/component/preview/DealRecordPane/store/actions";
import {
  addPersonCompanies,
  addPersonDeals,
} from "@app/component/preview/PersonRecordPane/store/actions";
import StreetViewModal from "@app/component/StreetViewModal";

import Actions from "./Actions";
import messages from "./messages";

interface Props {
  actionKey: string | undefined;
  assignedCompanies?: Company[];
  assignedPeople?: Person[];
  deal?: Deal;
  entity: EntitySupportingCard;
  onAddCompanyChildCompanies: typeof addCompanyChildCompanies.request;
  onAddCompanyDeals: typeof addCompanyDeals.request;
  onAddCompanyParentCompany: typeof addCompanyParentCompany.request;
  onAddCompanyPeople: typeof addCompanyPeople.request;
  onAddDealParentCompany: typeof addDealParentCompany.request;
  onAddDealParentPerson: typeof addDealParentPerson.request;
  onAddPersonCompanies: typeof addPersonCompanies.request;
  onAddPersonDeals: typeof addPersonDeals.request;
  onFetchNotes?: () => void;
  onModalHide: () => void;
  parentCompany?: Company | null;
}

const ModalForActions = ({
  actionKey,
  assignedCompanies,
  assignedPeople,
  deal,
  entity,
  onAddCompanyChildCompanies,
  onAddCompanyDeals,
  onAddCompanyParentCompany,
  onAddCompanyPeople,
  onAddDealParentCompany,
  onAddDealParentPerson,
  onAddPersonCompanies,
  onAddPersonDeals,
  onFetchNotes,
  onModalHide,
  parentCompany,
}: Props) => {
  const intl = useIntl();

  const [primaryCompanyId, setPrimaryCompanyId] = useState<Company["id"] | undefined>(
    entity.entity === EntityType.PERSON ? (entity.primaryAccountId ?? undefined) : undefined
  );

  const [primaryPersonId, setPrimaryPersonId] = useState<Person["id"] | undefined>(
    entity.entity === EntityType.COMPANY ? (entity.primaryContactId ?? undefined) : undefined
  );

  const handleSelectPeople = useCallback(
    (peopleIds: Person["id"][], removedPeopleIds?: Person["id"][]) => {
      if (entity.entity === EntityType.COMPANY) {
        onAddCompanyPeople({ id: entity.id, peopleIds, primaryPersonId, removedPeopleIds });
      } else {
        onAddDealParentPerson({ id: entity.id, parentPersonId: peopleIds[0] });
      }
    },
    [entity, primaryPersonId, onAddCompanyPeople, onAddDealParentPerson]
  );

  const handleSelectDeals = useCallback(
    (dealIds: Deal["id"][], removedDealsIds?: Deal["id"][]) => {
      if (entity.entity === EntityType.COMPANY) {
        onAddCompanyDeals({ id: entity.id, dealIds, removedDealsIds });
      } else {
        onAddPersonDeals({ id: entity.id, dealIds, removedDealsIds });
      }
    },
    [entity, onAddCompanyDeals, onAddPersonDeals]
  );

  const handleSelectCompany = useCallback(
    (companyIds: Company["id"][], removedCompanyIds?: Company["id"][]) => {
      if (actionKey === Actions.MANAGE_COMPANIES || actionKey === Actions.MANAGE_PARENT_COMPANY) {
        if (entity.entity === EntityType.COMPANY) {
          onAddCompanyParentCompany({ id: entity.id, parentCompanyId: companyIds[0] });
        } else if (entity.entity === EntityType.PERSON) {
          onAddPersonCompanies({
            id: entity.id,
            companyIds,
            primaryCompanyId,
            removedCompanyIds,
          });
        } else if (entity.entity === EntityType.DEAL) {
          onAddDealParentCompany({ id: entity.id, parentCompanyId: companyIds[0] });
        } else {
          invariant(false, `Invalid entity type ${(entity as EntitySupportingCard).entity}`);
        }
      } else {
        onAddCompanyChildCompanies({ id: entity.id, companyIds, removedCompanyIds });
      }
    },
    [
      actionKey,
      entity,
      onAddCompanyChildCompanies,
      onAddCompanyParentCompany,
      onAddDealParentCompany,
      onAddPersonCompanies,
      primaryCompanyId,
    ]
  );

  const handleChangeParentCompany = useCallback(
    (primaryCompanyId?: Company["id"]) => setPrimaryCompanyId(primaryCompanyId),
    []
  );

  const handleChangeParentPerson = useCallback(
    (primaryPersonId?: Person["id"]) => setPrimaryPersonId(primaryPersonId),
    []
  );

  useEffect(() => {
    setPrimaryCompanyId(
      entity.entity === EntityType.PERSON ? (entity.primaryAccountId ?? undefined) : undefined
    );
    setPrimaryPersonId(
      entity.entity === EntityType.COMPANY ? (entity.primaryContactId ?? undefined) : undefined
    );
  }, [entity]);

  const handleNoteModalHide = useCallback(
    (added: boolean, note?: Note) => {
      if (added && note) {
        onFetchNotes?.();
      }
      onModalHide();
    },
    [onModalHide, onFetchNotes]
  );

  switch (actionKey) {
    case Actions.ADD_DEAL:
      return (
        <DealAssociation
          disableLockedDeals
          disableNonEditableEntities
          fixedCompany={entity.entity === EntityType.COMPANY ? entity : undefined}
          fixedPerson={entity.entity === EntityType.PERSON ? entity : undefined}
          multiselect
          onHide={onModalHide}
          onSelect={handleSelectDeals}
        />
      );
    case Actions.ADD_NOTE:
      return (
        <AddNoteModal
          entityId={entity.id}
          entityName={entity.name}
          entityType={entity.entity}
          onHide={handleNoteModalHide}
          sourceObject={"sourceObject" in entity ? entity.sourceObject : undefined}
        />
      );
    case Actions.ADD_TO_ROUTE: {
      const entities =
        entity.entity === EntityType.COMPANY ? [entity as Company] : [entity as Person];
      return (
        <AddToRouteModal autoAssignOnCreate entities={entities} onHide={onModalHide} visible />
      );
    }
    case Actions.EDIT_STAGE:
      return <EditStageModal deal={entity as Deal} onHide={onModalHide} />;
    case Actions.MANAGE_CHILD_COMPANIES:
    case Actions.MANAGE_COMPANIES:
    case Actions.MANAGE_PARENT_COMPANY:
      return [
        Actions.MANAGE_CHILD_COMPANIES,
        Actions.MANAGE_COMPANIES,
        Actions.MANAGE_PARENT_COMPANY,
      ].includes(actionKey) ? (
        <CompanyAssociation
          assignedCompanies={
            actionKey === Actions.MANAGE_PARENT_COMPANY
              ? parentCompany
                ? [parentCompany]
                : undefined
              : assignedCompanies
          }
          associateWith={entity.id}
          canEditAssociatingRecord={entity.accessStatus?.update !== false}
          childSelect={actionKey === Actions.MANAGE_CHILD_COMPANIES}
          entityType={entity.entity}
          multiselect={
            Actions.MANAGE_CHILD_COMPANIES === actionKey || entity.entity === EntityType.PERSON
          }
          onHide={onModalHide}
          onSelect={handleSelectCompany}
          onUpdatePrimaryCompany={handleChangeParentCompany}
          primaryCompanyId={primaryCompanyId}
          supportsPrimaryEntity={entity.entity === EntityType.PERSON}
          title={
            entity.entity === EntityType.DEAL
              ? intl.formatMessage(messages.companyTitle)
              : actionKey === Actions.MANAGE_PARENT_COMPANY
                ? intl.formatMessage(messages.parentCompanyTitle)
                : actionKey === Actions.MANAGE_COMPANIES
                  ? intl.formatMessage(messages.companiesTitle)
                  : intl.formatMessage(messages.childCompanyTitle)
          }
        />
      ) : null;

    case Actions.MANAGE_GROUPS:
      return <GroupsModal entity={entity} entityType={entity.entity} onHide={onModalHide} />;

    case Actions.MANAGE_PEOPLE:
    case Actions.MANAGE_PERSON:
      return (
        <PeopleAssociation
          assignedPeople={assignedPeople}
          disableNonEditableEntities={entity.entity === EntityType.COMPANY}
          fixedCompany={entity.entity === EntityType.COMPANY ? entity : undefined}
          multiselect={actionKey === Actions.MANAGE_PEOPLE}
          onHide={onModalHide}
          onSelect={handleSelectPeople}
          onUpdatePrimaryPerson={handleChangeParentPerson}
          primaryPersonId={primaryPersonId}
          supportsPrimaryEntity={entity.entity === EntityType.COMPANY}
        />
      );

    case Actions.MARK_LOSS:
    case Actions.MARK_WON:
      return deal ? <EditStageModal deal={deal} fixedFunnelStage onHide={onModalHide} /> : null;

    case Actions.STREET_VIEW: {
      const coordinates = entity.geoPoint
        ? convertGeoPointToLatLngLiteral(entity.geoPoint)
        : undefined;
      return (
        <StreetViewModal
          coordinates={coordinates}
          entity={entity as Company | Person}
          onHide={onModalHide}
          open
        />
      );
    }
    default:
      return null;
  }
};

const mapDispatchToProps = {
  onAddCompanyChildCompanies: addCompanyChildCompanies.request,
  onAddCompanyDeals: addCompanyDeals.request,
  onAddCompanyParentCompany: addCompanyParentCompany.request,
  onAddCompanyPeople: addCompanyPeople.request,
  onAddDealParentCompany: addDealParentCompany.request,
  onAddDealParentPerson: addDealParentPerson.request,
  onAddPersonCompanies: addPersonCompanies.request,
  onAddPersonDeals: addPersonDeals.request,
};

export default connect(null, mapDispatchToProps)(ModalForActions);
